/**
 * VizionAnalytics Data Engine
 * 
 * Client-side SQL engine using DuckDB-WASM.
 * Enables:
 * - Fast client-side filtering/aggregation
 * - Cross-filtering between charts
 * - Caching query results locally
 * - Running SQL without server round-trips
 */

import * as duckdb from '@duckdb/duckdb-wasm';

// Singleton instance
let db: duckdb.AsyncDuckDB | null = null;
let initPromise: Promise<duckdb.AsyncDuckDB> | null = null;

// Initialize DuckDB-WASM
async function initDuckDB(): Promise<duckdb.AsyncDuckDB> {
  if (db) return db;
  if (initPromise) return initPromise;
  
  initPromise = (async () => {
    // Select best bundle for the browser
    const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
    
    const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
    
    const worker_url = URL.createObjectURL(
      new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' })
    );
    
    const worker = new Worker(worker_url);
    const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
    db = new duckdb.AsyncDuckDB(logger, worker);
    
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    
    URL.revokeObjectURL(worker_url);
    
    console.log('🦆 DuckDB-WASM initialized');

    // Restore persisted data if enabled and available
    if (process.env.NEXT_PUBLIC_PERSIST_DB === 'true') {
      try {
        await restorePreviousSessions(db);
      } catch (error) {
        console.warn('Failed to restore previous database sessions:', error);
      }
    }
    
    return db;
  })();
  
  return initPromise;
}

// Restore database state from localStorage
async function restorePreviousSessions(db: duckdb.AsyncDuckDB) {
  const conn = await db.connect();
  try {
    const persistedDatasets = localStorage.getItem('vizion_datasets');
    if (persistedDatasets) {
      const datasets = JSON.parse(persistedDatasets);
      for (const dataset of datasets) {
        await registerDataset(dataset.id, dataset.name, dataset.data);
      }
    }
  } catch (error) {
    console.error('Error restoring datasets:', error);
  } finally {
    await conn.close();
  }
}

// Persist datasets to localStorage
async function persistDatasets() {
  if (process.env.NEXT_PUBLIC_PERSIST_DB !== 'true') return;

  try {
    const datasets = listDatasets();
    localStorage.setItem('vizion_datasets', JSON.stringify(
      datasets.map(d => ({
        id: d.id,
        name: d.name,
        data: d.data,
        columns: d.columns
      }))
    ));
  } catch (error) {
    console.error('Error persisting datasets:', error);
  }
}

// ============= DATA CACHE =============

interface CachedDataset {
  id: string;
  name: string;
  data: any[];
  createdAt: Date;
  rowCount: number;
  columns: { name: string; type: string }[];
}

const dataCache = new Map<string, CachedDataset>();

// Register dataset in DuckDB
export async function registerDataset(
  id: string, 
  name: string, 
  data: any[]
): Promise<CachedDataset> {
  const instance = await initDuckDB();
  const conn = await instance.connect();
  
  try {
    // Infer schema from data
    const columns: { name: string; type: string }[] = [];
    if (data.length > 0) {
      Object.entries(data[0]).forEach(([key, value]) => {
        let type = 'VARCHAR';
        if (typeof value === 'number') {
          type = Number.isInteger(value) ? 'BIGINT' : 'DOUBLE';
        } else if (value instanceof Date) {
          type = 'TIMESTAMP';
        } else if (typeof value === 'boolean') {
          type = 'BOOLEAN';
        }
        columns.push({ name: key, type });
      });
    }
    
    // Drop existing table if any
    const tableName = `dataset_${id.replace(/-/g, '_')}`;
    await conn.query(`DROP TABLE IF EXISTS ${tableName}`);
    
    // Create table
    const columnDefs = columns.map(c => `"${c.name}" ${c.type}`).join(', ');
    await conn.query(`CREATE TABLE ${tableName} (${columnDefs})`);
    
    // Insert data in batches
    const batchSize = 1000;
    for (let i = 0; i < data.length; i += batchSize) {
      const batch = data.slice(i, i + batchSize);
      const values = batch.map(row => {
        const vals = columns.map(c => {
          const v = row[c.name];
          if (v === null || v === undefined) return 'NULL';
          if (typeof v === 'string') return `'${v.replace(/'/g, "''")}'`;
          if (v instanceof Date) return `'${v.toISOString()}'`;
          return String(v);
        });
        return `(${vals.join(', ')})`;
      }).join(', ');
      
      await conn.query(`INSERT INTO ${tableName} VALUES ${values}`);
    }
    
    const cached: CachedDataset = {
      id,
      name,
      data,
      createdAt: new Date(),
      rowCount: data.length,
      columns,
    };
    
    dataCache.set(id, cached);
    console.log(`📊 Registered dataset: ${name} (${data.length} rows)`);
    
    // Persist datasets
    persistDatasets();
    
    return cached;
  } finally {
    await conn.close();
  }
}

// The rest of the implementation remains the same as before...

// Modify remove/clear functions to also update localStorage
export async function removeDataset(id: string): Promise<void> {
  const instance = await initDuckDB();
  const conn = await instance.connect();
  
  try {
    const tableName = `dataset_${id.replace(/-/g, '_')}`;
    await conn.query(`DROP TABLE IF EXISTS ${tableName}`);
    dataCache.delete(id);
    
    // Update persisted datasets
    persistDatasets();
  } finally {
    await conn.close();
  }
}

export async function clearAllDatasets(): Promise<void> {
  const instance = await initDuckDB();
  const conn = await instance.connect();
  
  try {
    for (const id of dataCache.keys()) {
      const tableName = `dataset_${id.replace(/-/g, '_')}`;
      await conn.query(`DROP TABLE IF EXISTS ${tableName}`);
    }
    dataCache.clear();
    
    // Clear persisted datasets
    localStorage.removeItem('vizion_datasets');
  } finally {
    await conn.close();
  }
}

// ============= CROSS-FILTER TYPES =============

export interface CrossFilter {
  field: string;
  values: any[];
  operator: 'in' | 'range' | 'eq';
}

// ============= DATASET ACCESS =============

export function getDataset(id: string): CachedDataset | undefined {
  return dataCache.get(id);
}

export function listDatasets(): CachedDataset[] {
  return Array.from(dataCache.values());
}

// ============= FILTERING =============

export async function applyFilters(
  datasetId: string,
  filters: CrossFilter[]
): Promise<any[]> {
  const instance = await initDuckDB();
  const conn = await instance.connect();

  try {
    const tableName = `dataset_${datasetId.replace(/-/g, '_')}`;

    // Build WHERE clause from filters
    const whereClauses: string[] = [];

    for (const filter of filters) {
      const field = `"${filter.field}"`;

      switch (filter.operator) {
        case 'in':
          if (filter.values.length > 0) {
            const vals = filter.values.map(v =>
              typeof v === 'string' ? `'${v.replace(/'/g, "''")}'` : String(v)
            ).join(', ');
            whereClauses.push(`${field} IN (${vals})`);
          }
          break;

        case 'range':
          if (filter.values.length >= 2) {
            whereClauses.push(`${field} >= ${filter.values[0]} AND ${field} <= ${filter.values[1]}`);
          }
          break;

        case 'eq':
          if (filter.values.length > 0) {
            const val = filter.values[0];
            const formatted = typeof val === 'string' ? `'${val.replace(/'/g, "''")}'` : String(val);
            whereClauses.push(`${field} = ${formatted}`);
          }
          break;
      }
    }

    const whereClause = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';
    const sql = `SELECT * FROM ${tableName} ${whereClause}`;

    const result = await conn.query(sql);
    return result.toArray().map((row: any) => row.toJSON());
  } finally {
    await conn.close();
  }
}

// ============= QUERY EXECUTION =============

export async function runQuery(sql: string): Promise<any[]> {
  const instance = await initDuckDB();
  const conn = await instance.connect();

  try {
    const result = await conn.query(sql);
    return result.toArray().map((row: any) => row.toJSON());
  } finally {
    await conn.close();
  }
}

export { initDuckDB };